// =============================================================================
// ManagerDashboard.jsx  (Phase 2 — with Manager Edit + Report Builder)
// =============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Paper, Stack, MenuItem, Select, FormControl,
    InputLabel, CircularProgress, Divider, Grid, IconButton, Avatar,
    Tooltip, Badge, Button, Snackbar, Alert,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    ArrowForwardIos as ArrowIcon,
    ReportProblem as IssueIcon,
    Assignment as AobIcon,
    FlashOn as PresentIcon,
    AutoGraph as NextIcon,
    CheckCircle as DoneIcon,
    Pending as PendingIcon,
    Edit as EditIcon,
    Description as ReportBuilderIcon,
} from '@mui/icons-material';

import {
    apiGetByWeek,
    apiGetAvailableFilters,
    apiGetSubmissionById,
} from '../requests/weeklyUpdateRequests';

import ManagerEditDrawer from './ManagerEditDrawer';
import ReportBuilder     from './ReportBuilder';

// ─────────────────────────────────────────────────────────────────────────────

const ManagerDashboard = () => {

    // ── Filter state ──────────────────────────────────────────────────────────
    const [availableFilters, setAvailableFilters] = useState({ years: [], byYear: {}, allUsers: [] });
    const [submissions,      setSubmissions]      = useState([]);
    const [selectedYear,     setSelectedYear]     = useState('');
    const [selectedWeek,     setSelectedWeek]     = useState('');
    const [selectedUser,     setSelectedUser]     = useState('ALL');
    const [loadingFilters,   setLoadingFilters]   = useState(true);
    const [loadingData,      setLoadingData]      = useState(false);

    // ── Cascading derived filters ──────────────────────────────────────────────
    // Weeks available for the selected year
    const availableWeeks = useMemo(() =>
        availableFilters.byYear?.[selectedYear]?.weeks ?? [],
        [availableFilters, selectedYear]
    );

    // Users who submitted in the selected year+week (for the Member dropdown)
    const availableUsers = useMemo(() => {
        const submittedIds = availableFilters.byYear?.[selectedYear]?.byWeek?.[selectedWeek] ?? [];
        return availableFilters.allUsers.filter((u) => submittedIds.includes(u.id));
    }, [availableFilters, selectedYear, selectedWeek]);

    // ── Edit drawer state ─────────────────────────────────────────────────────
    const [editOpen,        setEditOpen]        = useState(false);
    const [editSubmission,  setEditSubmission]  = useState(null);
    const [editLoading,     setEditLoading]     = useState(false);

    // ── Report builder state ──────────────────────────────────────────────────
    const [reportOpen, setReportOpen] = useState(false);

    // ── Snackbar ──────────────────────────────────────────────────────────────
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSnack  = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnack = () => setSnackbar((prev) => ({ ...prev, open: false }));

    // ── Load filters on mount ─────────────────────────────────────────────────
    useEffect(() => {
        const loadMetadata = async () => {
            setLoadingFilters(true);
            try {
                const res = await apiGetAvailableFilters();
                const { years, byYear, allUsers } = res.data;
                setAvailableFilters({ years: years ?? [], byYear: byYear ?? {}, allUsers: allUsers ?? [] });

                // Default to the most recent year, then its most recent week
                if (years?.length > 0) {
                    const latestYear  = years[0]; // already DESC from backend
                    const latestWeeks = byYear?.[latestYear]?.weeks ?? [];
                    const latestWeek  = latestWeeks.length > 0 ? latestWeeks[latestWeeks.length - 1] : '';
                    setSelectedYear(latestYear);
                    setSelectedWeek(latestWeek);
                }
            } catch (err) {
                console.error('Error loading filters:', err);
            } finally {
                setLoadingFilters(false);
            }
        };
        loadMetadata();
    }, []);

    // ── Cascade: when year changes, reset week to the last available for that year ──
    useEffect(() => {
        if (!selectedYear || !availableFilters.byYear) return;
        const weeks = availableFilters.byYear[selectedYear]?.weeks ?? [];
        const lastWeek = weeks.length > 0 ? weeks[weeks.length - 1] : '';
        setSelectedWeek(lastWeek);
        setSelectedUser('ALL');
    }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Cascade: when week changes, reset member filter ───────────────────────
    useEffect(() => {
        setSelectedUser('ALL');
    }, [selectedWeek]);

    // ── Load submissions ──────────────────────────────────────────────────────
    const fetchReports = useCallback(async () => {
        if (!selectedYear || !selectedWeek) return;
        setLoadingData(true);
        try {
            const userId = selectedUser === 'ALL' ? null : selectedUser;
            const res    = await apiGetByWeek(selectedWeek, selectedYear, userId);
            setSubmissions(res.data ?? []);
        } catch (err) {
            console.error('Error loading reports:', err);
        } finally {
            setLoadingData(false);
        }
    }, [selectedYear, selectedWeek, selectedUser]);

    useEffect(() => {
        if (!loadingFilters) fetchReports();
    }, [selectedYear, selectedWeek, selectedUser, loadingFilters]);

    // ── Submission tracker ────────────────────────────────────────────────────
    const submissionStatus = useMemo(() => {
        const submittedIds = submissions.map((s) => String(s.userId));
        return availableFilters.allUsers.map((user) => ({
            ...user,
            hasSubmitted: submittedIds.includes(String(user.id)),
        }));
    }, [availableFilters.allUsers, submissions]);

    const totalSubmitted = submissionStatus.filter((s) => s.hasSubmitted).length;

    // ── Open edit drawer ──────────────────────────────────────────────────────
    const handleOpenEdit = async (submissionId) => {
        setEditOpen(true);
        setEditSubmission(null);
        setEditLoading(true);
        try {
            const res = await apiGetSubmissionById(submissionId);
            setEditSubmission(res.data);
        } catch (err) {
            console.error('Error loading submission for edit:', err);
            setEditSubmission(null);
        } finally {
            setEditLoading(false);
        }
    };

    // ── Called after a successful manager edit ────────────────────────────────
    const handleEditSaved = (updatedSub) => {
        // Merge the updated submission back into the list so UI reflects changes immediately
        setSubmissions((prev) =>
            prev.map((s) => s.id === updatedSub.id ? { ...s, ...updatedSub } : s)
        );
        setEditOpen(false);
        showSnack(`Submission for ${updatedSub.userName} updated successfully.`);
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (loadingFilters) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
                <CircularProgress size={30} />
                <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading dashboard...
                </Typography>
            </Box>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 4, bgcolor: '#f4f7f9', minHeight: '100vh' }}>

            {/* ── HEADER & FILTERS ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -1 }}>
                        Manager Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Week <b>{selectedWeek}</b> of <b>{selectedYear}</b> &bull;&nbsp;
                        {totalSubmitted}/{availableFilters.allUsers.length} Received
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    {/* Report Builder trigger */}
                    <Tooltip title="Build & export final report">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<ReportBuilderIcon />}
                                onClick={() => setReportOpen(true)}
                                disabled={submissions.length === 0}
                                size="small"
                                sx={{ borderRadius: 2, fontWeight: 700 }}
                            >
                                Report Builder
                            </Button>
                        </span>
                    </Tooltip>

                    {/* Filter bar */}
                    <Stack direction="row" spacing={2}
                        sx={{ bgcolor: 'white', p: 1.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        {/* Year — static list */}
                        <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                            <InputLabel sx={{ fontSize: 12 }}>Year</InputLabel>
                            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}
                                disableUnderline sx={{ fontWeight: 600 }}>
                                {availableFilters.years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {/* Week — cascades from Year */}
                        <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                            <InputLabel sx={{ fontSize: 12 }}>Week</InputLabel>
                            <Select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}
                                disableUnderline sx={{ fontWeight: 600 }}
                                disabled={availableWeeks.length === 0}>
                                {availableWeeks.map((w) => <MenuItem key={w} value={w}>W{w}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {/* Member — cascades from Year + Week (only users who submitted) */}
                        <FormControl size="small" variant="standard" sx={{ minWidth: 160 }}>
                            <InputLabel sx={{ fontSize: 12 }}>Filter Member</InputLabel>
                            <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}
                                disableUnderline sx={{ fontWeight: 600 }}
                                disabled={!selectedWeek}>
                                <MenuItem value="ALL"><b>All Members</b></MenuItem>
                                {availableUsers.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <IconButton onClick={fetchReports} color="primary" disabled={loadingData}>
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>

            {/* ── SUBMISSION TRACKER ── */}
            <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 3, border: '1px solid #e0e6ed', bgcolor: 'white' }}>
                <Typography variant="caption" fontWeight={800} color="text.secondary"
                    sx={{ mb: 2, display: 'block', ml: 1, letterSpacing: 1 }}>
                    TEAM SUBMISSION TRACKER
                </Typography>
                <Stack direction="row" spacing={3} sx={{ overflowX: 'auto', pb: 1 }}>
                    {submissionStatus.map((m) => (
                        <Tooltip key={m.id} title={m.hasSubmitted ? 'Report Received' : 'Pending Report'}>
                            <Stack alignItems="center" spacing={1} sx={{ minWidth: 70, opacity: m.hasSubmitted ? 1 : 0.4 }}>
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        m.hasSubmitted
                                            ? <DoneIcon sx={{ fontSize: 14, color: 'white', bgcolor: '#2e7d32', borderRadius: '50%' }} />
                                            : <PendingIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
                                    }
                                >
                                    <Avatar sx={{ bgcolor: m.hasSubmitted ? '#1976d2' : '#ccc', fontWeight: 700 }}>
                                        {m.name.charAt(0)}
                                    </Avatar>
                                </Badge>
                                <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 80 }}>
                                    {m.name}
                                </Typography>
                            </Stack>
                        </Tooltip>
                    ))}
                </Stack>
            </Paper>

            {/* ── SUBMISSION CARDS ── */}
            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={5}>
                    {submissions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 10, opacity: 0.5 }}>
                            <Typography variant="h6">No reports found for this selection.</Typography>
                        </Box>
                    ) : (
                        submissions.map((sub) => (
                            <Box key={sub.userId}>
                                {/* User label + Edit button */}
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, ml: 1 }}>
                                    <Typography variant="h6" fontWeight={800} color="#333">
                                        {sub.userName}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                                        ({sub.userEmail})
                                    </Typography>
                                    <Tooltip title="Edit this submission">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(sub.id)}
                                                sx={{
                                                    ml: 0.5,
                                                    color: 'text.secondary',
                                                    '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>

                                {/* Submission card */}
                                <Paper elevation={0} sx={{
                                    borderRadius: 3, border: '1px solid #e0e6ed',
                                    overflow: 'hidden', bgcolor: 'white',
                                }}>
                                    <Grid container>

                                        {/* PRESENT WEEK */}
                                        <Grid item xs={12} md={4} sx={{ p: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
                                                <PresentIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight={800}
                                                    color="black" sx={{ letterSpacing: 0.5 }}>
                                                    PRESENT WEEK
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={2}>
                                                {sub.presentWeekActivities?.map((act, i) => (
                                                    <Box key={i} sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #eee' }}>
                                                        <Typography variant="caption" fontWeight={900} color="text.secondary"
                                                            sx={{ display: 'block', mb: 0.5 }}>
                                                            {act.technology} &bull; {act.vendor}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                                            {act.description}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Grid>

                                        {/* Arrow divider */}
                                        <Grid item md={0.1} sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            justifyContent: 'center', alignItems: 'center', position: 'relative',
                                        }}>
                                            <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1.5 }} />
                                            <Box sx={{
                                                position: 'absolute', bgcolor: 'white',
                                                border: '1px solid #e0e6ed', borderRadius: '50%', p: 0.4, display: 'flex',
                                            }}>
                                                <ArrowIcon sx={{ fontSize: 10, color: '#999' }} />
                                            </Box>
                                        </Grid>

                                        {/* NEXT WEEK */}
                                        <Grid item xs={12} md={4} sx={{ p: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
                                                <NextIcon fontSize="small" color="primary" />
                                                <Typography variant="subtitle2" fontWeight={800}
                                                    sx={{ color: '#000000', letterSpacing: 0.5 }}>
                                                    NEXT WEEK
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={2}>
                                                {sub.nextWeekActivities?.map((act, i) => (
                                                    <Box key={i} sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #eee' }}>
                                                        <Typography variant="caption" fontWeight={900} color="text.secondary"
                                                            sx={{ display: 'block', mb: 0.5 }}>
                                                            {act.technology} &bull; {act.vendor}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                                            {act.description}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Grid>

                                        {/* ISSUES & AOB sidebar */}
                                        <Grid item xs={12} md={3.9}
                                            sx={{ bgcolor: '#fafafa', p: 3, borderLeft: '1px solid #e0e6ed' }}>
                                            <Stack spacing={4}>
                                                <Box>
                                                    <Typography variant="caption" fontWeight={900} color="error"
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <IssueIcon fontSize="inherit" /> ISSUES & RISKS
                                                    </Typography>
                                                    {sub.issues?.map((is, i) => (
                                                        <Typography key={i} variant="body2"
                                                            sx={{ mb: 1, pl: 1, borderLeft: '2px solid #d32f2f' }}>
                                                            {is.description}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                                <Divider />
                                                <Box>
                                                    <Typography variant="caption" fontWeight={900} color="text.secondary"
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <AobIcon fontSize="inherit" /> OTHER BUSINESS
                                                    </Typography>
                                                    {sub.aob?.map((a, i) => (
                                                        <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                                                            &bull; {a.description}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        ))
                    )}
                </Stack>
            )}

            {/* ── MANAGER EDIT DRAWER ── */}
            <ManagerEditDrawer
                open={editOpen}
                submission={editSubmission}
                loading={editLoading}
                onClose={() => setEditOpen(false)}
                onSaved={handleEditSaved}
            />

            {/* ── REPORT BUILDER DRAWER ── */}
            <ReportBuilder
                open={reportOpen}
                submissions={submissions}
                weekNumber={selectedWeek}
                year={selectedYear}
                onClose={() => setReportOpen(false)}
            />

            {/* ── SNACKBAR ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={closeSnack} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ManagerDashboard;